package features.schedule.handlers

import kotlinx.browser.document
import kotlinx.html.*
import kotlinx.html.dom.append
import kotlinx.html.js.onClickFunction
import shared.ui.ModalManager
import AppState
import Router
import org.w3c.dom.events.Event

// Schedule Cell Selection Handlers
fun handleScheduleCellClick(event: Event, memberId: String, day: Int) {
    val cellId = "$memberId:$day"
    val shiftKey = event.asDynamic().shiftKey as? Boolean ?: false
    
    if (shiftKey && AppState.lastSelectedCell != null) {
        // Range selection
        val lastCell = AppState.lastSelectedCell!!
        val (lastMemberId, lastDay) = lastCell.split(":")
        
        // Select all cells in range
        val members = AppState.members[AppState.currentTeam?.id] ?: emptyList()
        val memberIndex = members.indexOfFirst { it.id == memberId }
        val lastMemberIndex = members.indexOfFirst { it.id == lastMemberId }
        
        val startMemberIdx = minOf(memberIndex, lastMemberIndex)
        val endMemberIdx = maxOf(memberIndex, lastMemberIndex)
        val startDay = minOf(day, lastDay.toInt())
        val endDay = maxOf(day, lastDay.toInt())
        
        for (i in startMemberIdx..endMemberIdx) {
            for (d in startDay..endDay) {
                AppState.selectedScheduleCells.add("${members[i].id}:$d")
            }
        }
    } else {
        // Toggle single cell
        if (AppState.selectedScheduleCells.contains(cellId)) {
            AppState.selectedScheduleCells.remove(cellId)
        } else {
            AppState.selectedScheduleCells.add(cellId)
        }
    }
    
    AppState.lastSelectedCell = cellId
    Router.render()
}

fun bulkAssignShift(shiftCode: String) {
    val teamId = AppState.currentTeam?.id ?: return
    var schedule = AppState.schedules[teamId]
    
    if (schedule == null) {
        val now = kotlin.js.Date()
        schedule = features.schedule.domain.Schedule(
            year = now.getFullYear().toInt(),
            month = (now.getMonth() + 1).toInt(),
            assignments = mutableMapOf()
        )
        AppState.schedules[teamId] = schedule
    }
    
    // Apply shift to all selected cells
    AppState.selectedScheduleCells.forEach { cellId ->
        val (memberId, day) = cellId.split(":")
        val memberAssignments = schedule.assignments.getOrPut(memberId) { mutableMapOf() }
        memberAssignments[day.toInt()] = shiftCode
    }
    
    // Save to Firestore
    features.schedule.data.ScheduleRepository.saveSchedule(teamId, schedule)
        .then {
            console.log("Bulk assignment saved successfully")
            AppState.selectedScheduleCells.clear()
            AppState.lastSelectedCell = null
            Router.render()
        }
        .catch { error ->
            console.error("Error saving bulk assignment:", error)
            kotlinx.browser.window.alert("Failed to save schedule")
        }
}

// Schedule Rendering Functions
fun renderScheduleByDate(memberId: String) {
    val teamId = AppState.currentTeam?.id ?: return
    val member = AppState.members[teamId]?.find { it.id == memberId } ?: return
    val schedule = AppState.schedules[teamId]
    val shifts = AppState.shiftTemplates[teamId] ?: emptyList()
    
    val container = document.getElementById("schedule-view-container") as? org.w3c.dom.HTMLElement ?: return
    container.innerHTML = ""
    
    container.append {
        div("space-y-2") {
            for (day in 1..31) {
                val shiftCode = schedule?.assignments?.get(memberId)?.get(day) ?: "-"
                val shift = shifts.find { it.code == shiftCode }
                
                div("flex items-center justify-between p-3 bg-dark-700 rounded-lg hover:bg-dark-600 transition cursor-pointer") {
                    attributes["data-day"] = day.toString()
                    onClickFunction = { openShiftAssignment(memberId, day) }
                    
                    span("text-slate-300") { +"Day $day" }
                    span("px-3 py-1 rounded-md text-xs font-bold") {
                        style = "background: ${shift?.color ?: "#334155"}; color: white"
                        +shiftCode
                    }
                }
            }
        }
    }
}

fun renderScheduleByShift(memberId: String) {
    val teamId = AppState.currentTeam?.id ?: return
    val member = AppState.members[teamId]?.find { it.id == memberId } ?: return
    val schedule = AppState.schedules[teamId]
    val shifts = AppState.shiftTemplates[teamId] ?: emptyList()
    
    val container = document.getElementById("schedule-view-container") as? org.w3c.dom.HTMLElement ?: return
    container.innerHTML = ""
    
    // Group days by shift
    val daysByShift = mutableMapOf<String, MutableList<Int>>()
    for (day in 1..31) {
        val shiftCode = schedule?.assignments?.get(memberId)?.get(day) ?: "-"
        daysByShift.getOrPut(shiftCode) { mutableListOf() }.add(day)
    }
    
    container.append {
        div("space-y-3") {
            shifts.forEach { shift ->
                val days = daysByShift[shift.code] ?: emptyList()
                if (days.isNotEmpty()) {
                    div("p-4 rounded-lg") {
                        style = "background: ${shift.color}20; border: 1px solid ${shift.color}40"
                        
                        div("flex items-center gap-2 mb-2") {
                            span("px-3 py-1 rounded-md text-sm font-bold") {
                                style = "background: ${shift.color}; color: white"
                                +shift.code
                            }
                            span("text-slate-300 text-sm") { +"${shift.startTime} - ${shift.endTime}" }
                        }
                        
                        div("flex flex-wrap gap-1 mt-2") {
                            days.forEach { day ->
                                span("px-2 py-1 bg-dark-700 rounded text-xs text-slate-300") { +"$day" }
                            }
                        }
                    }
                }
            }
            
            // Show unassigned days
            val unassignedDays = daysByShift["-"] ?: emptyList()
            if (unassignedDays.isNotEmpty()) {
                div("p-4 bg-dark-700 rounded-lg") {
                    span("text-slate-400 text-sm font-bold") { +"Unassigned Days" }
                    div("flex flex-wrap gap-1 mt-2") {
                        unassignedDays.forEach { day ->
                            span("px-2 py-1 bg-dark-600 rounded text-xs text-slate-400") { +"$day" }
                        }
                    }
                }
            }
        }
    }
}

fun openShiftAssignment(memberId: String, day: Int) {
    // Store in modal data attributes or similar
    ModalManager.show("assign-shift-modal")
}

fun assignShift(memberId: String, day: Int, code: String) {
    val teamId = AppState.currentTeam?.id ?: return
    var schedule = AppState.schedules[teamId]
    
    if (schedule == null) {
        val now = kotlin.js.Date()
        schedule = features.schedule.domain.Schedule(
            year = now.getFullYear().toInt(),
            month = (now.getMonth() + 1).toInt(),
            assignments = mutableMapOf()
        )
        AppState.schedules[teamId] = schedule
    }
    
    val memberAssignments = schedule.assignments.getOrPut(memberId) { mutableMapOf() }
    memberAssignments[day] = code
    
    features.schedule.data.ScheduleRepository.saveSchedule(teamId, schedule)
        .then {
            console.log("Schedule saved successfully")
            // Close modal by hiding it
            val modal = kotlinx.browser.document.getElementById("assign-shift-modal")
            modal?.asDynamic()?.style?.display = "none"
            renderScheduleByDate(memberId)
            Router.render()
        }
        .catch { error ->
            console.error("Error saving schedule:", error)
            kotlinx.browser.window.alert("Failed to save schedule")
        }
}

fun saveCustomShift(memberId: String, day: Int) {
    val startHour = (document.getElementById("custom-start-hour") as? org.w3c.dom.HTMLSelectElement)?.value ?: "00"
    val startMinute = (document.getElementById("custom-start-minute") as? org.w3c.dom.HTMLSelectElement)?.value ?: "00"
    val endHour = (document.getElementById("custom-end-hour") as? org.w3c.dom.HTMLSelectElement)?.value ?: "00"
    val endMinute = (document.getElementById("custom-end-minute") as? org.w3c.dom.HTMLSelectElement)?.value ?: "00"
    
    val customCode = "$startHour:$startMinute-$endHour:$endMinute"
    assignShift(memberId, day, customCode)
}

fun clearShift(memberId: String, day: Int) {
    assignShift(memberId, day, "-")
}
